import React, { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from '../Container';
import Image from '../Image';
import Selector from './Selector';
import Slider from '../Slider';
import ColorPicker from '../ColorPicker';
import EmptyButton from '../EmptyButton';
import Icon from '../Icon';
import Text from '../Text';
import Button from '../Button';
import { load, crop } from '../../image';

const sizeToParent = (parent, dimensions) => {
  const { width: parentWidth, height: parentHeight } = parent;
  let { width } = dimensions;
  let { height } = dimensions;
  const aspectRatio = width / height;

  if (width !== parentWidth) {
    width = parentWidth;
    height = width / aspectRatio;
  }
  if (height > parentHeight) {
    height = parentHeight;
    width = height * aspectRatio;
  }

  return {
    width,
    height,
  };
};

const center = (parent, dimensions) => {
  const { width, height } = dimensions;
  const left = parent.width / 2 - width / 2 + (parent.left || 0);
  const top = parent.height / 2 - height / 2 + (parent.top || 0);

  return {
    left,
    top,
  };
};

const sizeToFit = (parent, child) => {
  const size = sizeToParent(parent, child);
  const position = center(parent, size);
  return {
    ...child,
    ...size,
    ...position,
  };
};

const initCropper = (parent, style) => ({
  ...style,
  ...center(parent, style),
});

const initImage = async (parent, style) => sizeToFit(parent, style);

const zoomTo = (zoom, { naturalWidth, naturalHeight }) => ({
  width: naturalWidth * (zoom / 100),
  height: naturalHeight * (zoom / 100),
});

const calculateZoom = (imageStyle, original) => {
  if (!(imageStyle && original)) {
    return null;
  }
  return (imageStyle.width / original.naturalWidth) * 100;
};

const positionInternalImage = (cropperStyle, imageStyle) => {
  if (!(cropperStyle && imageStyle)) {
    return null;
  }
  return {
    ...imageStyle,
    left: imageStyle.left - cropperStyle.left,
    top: imageStyle.top - cropperStyle.top,
  };
};

const cropImage = async (
  src,
  cropperStyle,
  imageStyle,
  backgroundColor,
  cropRatio
) => {
  if (!(src && cropperStyle && imageStyle)) {
    return null;
  }

  return crop(
    src,
    (cropperStyle.left - imageStyle.left) * cropRatio,
    (cropperStyle.top - imageStyle.top) * cropRatio,
    cropperStyle.width * cropRatio,
    cropperStyle.height * cropRatio,
    imageStyle.width * cropRatio,
    imageStyle.height * cropRatio,
    backgroundColor
  );
};

const setElementStyle = (element, style) => {
  const e = element;
  ['top', 'left', 'width', 'height'].forEach(key => {
    e.style[key] = `${style[key]}px`;
  });
};

const setElementOpacity = (element, val) => {
  const e = element;
  if (e) {
    e.style.opacity = val;
  }
};

const getElementStyle = element =>
  ['top', 'left', 'width', 'height'].reduce(
    (aggregate, key) => ({
      ...aggregate,
      [key]: parseFloat(element.style[key]),
    }),
    {}
  );

const ImageEditor = ({
  src,
  onChanged,
  canvasProps,
  cropperSize,
  cropRatio,
  fixed,
  rounded,
  mode,
  scale,
  ...props
}) => {
  const canvasRef = useRef();
  const cropperRef = useRef();
  const imageRef = useRef();
  const internalImageRef = useRef();
  const imageSelectorRef = useRef();
  const cropperSelectorRef = useRef();

  const [original, setOriginal] = useState({});
  const [backgroundColor, setBackgroundColor] = useState();
  const [selected, setSelected] = useState();
  const [cropperStyle, setCropperStyle] = useState();
  const [internalImageStyle, setInternalImageStyle] = useState();
  const [imageStyle, setImageStyle] = useState();
  const [zoom, setZoom] = useState(100);

  const onColorChanged = useCallback(
    val => setBackgroundColor(val ? val.color : null),
    []
  );

  const onNoneSelected = useCallback(() => setSelected('none'), []);

  const onSelectorChanged = useCallback(
    ({ dragArgs: { state: dragState }, style }) => {
      setElementStyle(cropperRef.current, style);
      setElementStyle(
        internalImageRef.current,
        positionInternalImage(style, getElementStyle(imageRef.current))
      );
      setElementOpacity(cropperSelectorRef.current, 1);
      setElementOpacity(imageSelectorRef.current, 0);

      if (dragState === 'stopped') {
        setCropperStyle(style);
        setSelected('cropper');
      }
    },
    []
  );

  const onImageChanged = useCallback(
    ({ dragArgs: { state: dragState }, style }) => {
      setElementStyle(imageRef.current, style);
      setElementStyle(
        internalImageRef.current,
        positionInternalImage(getElementStyle(cropperRef.current), style)
      );
      setElementOpacity(cropperSelectorRef.current, 0);
      setElementOpacity(imageSelectorRef.current, 1);
      if (dragState === 'stopped') {
        setImageStyle(style);
        setSelected('image');
      }
    },
    []
  );

  const onZoomChanged = useCallback(
    ({ dragArgs: { state: dragState }, value }) => {
      if (!original) {
        return;
      }
      const style = {
        ...getElementStyle(imageRef.current),
        ...zoomTo(value * 2, original),
      };
      setElementStyle(imageRef.current, style);
      setElementStyle(imageSelectorRef.current, style);
      setElementStyle(
        internalImageRef.current,
        positionInternalImage(getElementStyle(cropperRef.current), style)
      );
      if (dragState === 'stopped') {
        setImageStyle(style);
      }
    },
    [original]
  );

  const onSizeToFitClicked = useCallback(() => {
    const style = sizeToFit(
      getElementStyle(cropperRef.current),
      getElementStyle(imageRef.current)
    );
    setElementStyle(imageRef.current, style);
    setElementStyle(
      internalImageRef.current,
      positionInternalImage(getElementStyle(cropperRef.current), style)
    );
    setImageStyle(style);
  }, []);

  const onEditComplete = useCallback(async () => {
    const croppedImage = await cropImage(
      src,
      cropperStyle,
      imageStyle,
      backgroundColor,
      cropRatio
    );
    onChanged(croppedImage);
  }, [backgroundColor, cropRatio, cropperStyle, imageStyle, onChanged, src]);

  useEffect(() => {
    const {
      current: { offsetWidth: width, offsetHeight: height },
    } = canvasRef;

    const initCropperStyle = initCropper(
      {
        width,
        height,
      },
      cropperSize
    );
    setCropperStyle(initCropperStyle);
    setZoom(val => {
      (async () => {
        const img = await load(src);
        const style = await initImage(initCropperStyle, {
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
        setOriginal(img);
        setImageStyle(style);
        setSelected(fixed ? 'image' : 'cropper');
      })();
      return val;
    });
  }, [cropperSize, fixed, onSizeToFitClicked, src]);

  useEffect(
    () =>
      setInternalImageStyle(() =>
        positionInternalImage(cropperStyle, imageStyle)
      ),
    [cropperStyle, imageStyle]
  );

  useEffect(
    () => setZoom(calculateZoom(imageStyle, original)),
    [imageStyle, original]
  );

  return (
    <Container {...props}>
      <Container display="flex" justifyContent="center" flexDirection="column">
        <Container display="flex" alignItems="center" paddingBottom="16px">
          <ColorPicker
            zIndex="1"
            onSelected={onColorChanged}
            selected={backgroundColor}
            text="Background Colour"
            width={mode !== 'desktop' ? '100%' : 'auto'}
          />
        </Container>
        <Container
          minHeight={mode !== 'desktop' ? '250px' : '400px'}
          minWidth={mode !== 'desktop' ? '250px' : '600px'}
          backgroundColor="#e9e9e9"
          {...canvasProps}
          ref={canvasRef}
          position="relative"
        >
          <Container
            position="absolute"
            top="0px"
            right="0px"
            bottom="0px"
            left="0px"
            overflow="hidden"
          >
            <Container
              transform={`scale(${scale})`}
              width="100%"
              height=" 100%"
            >
              <Container
                ref={imageRef}
                position="absolute"
                overflow="hidden"
                style={imageStyle}
              >
                <Image src={src} width="100%" />
                <Container
                  position="absolute"
                  top="0px"
                  left="0px"
                  right="0px"
                  bottom="0px"
                  backgroundColor="rgba(0, 0, 0, 0.5)"
                />
              </Container>
              <Container
                ref={cropperRef}
                position="absolute"
                overflow="hidden"
                style={cropperStyle}
                backgroundColor={backgroundColor || '#ffffff'}
                backgroundSize={backgroundColor ? 'auto' : '20px 20px'}
                backgroundPosition={
                  backgroundColor ? '0% 0%' : '0 0, 10px 10px'
                }
                borderRadius={rounded ? '100%' : '0'}
                backgroundImage={
                  backgroundColor
                    ? 'none'
                    : `linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd), 
                        linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd)`
                }
              >
                <Container
                  ref={internalImageRef}
                  position="absolute"
                  style={internalImageStyle}
                >
                  <Image src={src} width="100%" />
                </Container>
              </Container>
            </Container>
          </Container>

          <Container
            position="absolute"
            top="0px"
            right="0px"
            bottom="0px"
            left="0px"
            transform={`scale(${scale})`}
          >
            <Container
              position="absolute"
              top="0px"
              right="0px"
              bottom="0px"
              left="0px"
              onMouseDown={onNoneSelected}
            />
            <Selector
              ref={imageSelectorRef}
              style={imageStyle}
              aspectRatio={original.naturalWidth / original.naturalHeight}
              onChanged={onImageChanged}
              opacity={selected === 'image' ? 1 : 0}
            />
            {!fixed && (
              <Selector
                ref={cropperSelectorRef}
                style={cropperStyle}
                onChanged={onSelectorChanged}
                opacity={selected === 'cropper' ? 1 : 0}
              />
            )}
          </Container>
        </Container>
        <Container
          display="flex"
          alignItems="center"
          justifyContent={mode === 'desktop' ? 'space-between' : 'center'}
          alignContent="center"
          flexWrap="wrap"
          marginTop="10px"
        >
          <Container paddingTop="16px" paddingBottom="16px">
            <EmptyButton type="button" onClick={onSizeToFitClicked}>
              <Container
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  className="zmdi zmdi-fullscreen"
                  iconSize="medium"
                  iconColor="normal"
                  marginRight="small"
                />
                <Text color="normal" whiteSpace="nowrap">
                  Size To Fit
                </Text>
              </Container>
            </EmptyButton>
          </Container>
          <Container flex="1" padding="15px" minWidth="200px">
            <Slider
              zIndex="1"
              value={zoom / 2}
              onChanged={onZoomChanged}
              width="100%"
              flex="1"
            />
          </Container>
          <Container marginLeft="auto">
            <Button type="button" rounded secondary onClick={onEditComplete}>
              Done
            </Button>
          </Container>
        </Container>
      </Container>
    </Container>
  );
};

ImageEditor.propTypes = {
  src: PropTypes.string,
  onChanged: PropTypes.func.isRequired,
  canvasProps: PropTypes.shape({}),
  cropperSize: PropTypes.shape({}),
  fixed: PropTypes.bool,
  rounded: PropTypes.bool,
  cropRatio: PropTypes.number,
  scale: PropTypes.number,
  mode: PropTypes.string,
};

ImageEditor.defaultProps = {
  src: null,
  canvasProps: {},
  cropperSize: {
    width: 300,
    height: 300,
  },
  fixed: false,
  rounded: false,
  cropRatio: 1,
  scale: 1,
  mode: 'desktop',
};

export default ImageEditor;
